"use client";

import { useCallback, useRef } from "react";
import type { AgentEvent, AgentEventType, LessonContextInput } from "./types";
import { firesTutorMessage, getDelayMs, isSuppressed, isUnprompted } from "./triggers";
import type { PromptTrigger } from "./useAgentStream";

export type DebugEvent = {
  type: AgentEventType;
  ts: number;
  suppressed: boolean;
  delayMs: number;
};

import type { FireOptions } from "./useAgentStream";

export type UseEventQueueOptions = {
  fireRef: React.MutableRefObject<
    (userMessage?: string, trigger?: PromptTrigger, options?: FireOptions) => void
  >;
  lastFiredRef: React.MutableRefObject<number | null>;
  pendingDelayUntilRef: React.MutableRefObject<number | null>;
  lessonContextRef: React.MutableRefObject<LessonContextInput | null>;
  eventsRef: React.MutableRefObject<AgentEvent[]>;
  performanceRef: React.MutableRefObject<{ correct: number; incorrect: number; streak: number }>;
  /**
   * True once the tutor has spoken unprompted and the learner hasn't answered.
   * Set here, cleared by the learner's next message: a tip they ignored is a tip
   * they didn't want, so repeating it unasked just compounds the interruption.
   */
  awaitingReplyRef: React.MutableRefObject<boolean>;
};

export type UseEventQueueReturn = {
  dispatchEvent: (event: AgentEvent) => void;
  resetEventQueue: () => void;
  debugEventsRef: React.MutableRefObject<DebugEvent[]>;
};

export function useEventQueue({
  fireRef,
  lastFiredRef,
  pendingDelayUntilRef,
  lessonContextRef,
  eventsRef,
  performanceRef,
  awaitingReplyRef,
}: UseEventQueueOptions): UseEventQueueReturn {
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debugEventsRef = useRef<DebugEvent[]>([]);

  const resetEventQueue = useCallback(() => {
    debugEventsRef.current = [];
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
      pendingDelayUntilRef.current = null;
    }
  }, [pendingDelayUntilRef]);

  const dispatchEvent = useCallback(
    (event: AgentEvent) => {
      if (!lessonContextRef.current) return;

      eventsRef.current = [...eventsRef.current, event].slice(-10);

      if (event.type === "answer_submitted_correct") {
        performanceRef.current = {
          ...performanceRef.current,
          correct: performanceRef.current.correct + 1,
          streak: performanceRef.current.streak + 1,
        };
      } else if (event.type === "answer_submitted_incorrect") {
        performanceRef.current = {
          ...performanceRef.current,
          incorrect: performanceRef.current.incorrect + 1,
          streak: 0,
        };
      }

      const unprompted = isUnprompted(event.type);
      const suppressed =
        !firesTutorMessage(event.type) ||
        (unprompted && isSuppressed(lastFiredRef.current)) ||
        (unprompted && awaitingReplyRef.current);
      const delayMs = suppressed ? 0 : getDelayMs(event.type);

      debugEventsRef.current = [
        ...debugEventsRef.current,
        { type: event.type, ts: Date.now(), suppressed, delayMs },
      ].slice(-20);

      if (suppressed) return;

      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }

      pendingDelayUntilRef.current = delayMs > 0 ? Date.now() + delayMs : null;
      delayTimerRef.current = setTimeout(() => {
        delayTimerRef.current = null;
        pendingDelayUntilRef.current = null;
        // An unprompted turn never takes the screen: it streams into the
        // transcript and spins the collapsed launcher, and the learner reads it
        // when they choose to. Only a turn the learner asked for opens the panel.
        if (unprompted) awaitingReplyRef.current = true;
        fireRef.current(undefined, event.type, { open: !unprompted, nudge: unprompted });
      }, delayMs);
    },
    [
      fireRef,
      lastFiredRef,
      pendingDelayUntilRef,
      lessonContextRef,
      eventsRef,
      performanceRef,
      awaitingReplyRef,
    ],
  );

  return { dispatchEvent, resetEventQueue, debugEventsRef };
}
